import {
  type HiddenTime,
  __setClockForTests,
  hiddenMsThrough,
  newHiddenTime,
  now,
  readStoredState,
  recordHidden,
  writeStoredState,
} from "./dwellClock";
import { emitEndVisitAcceptEvent, emitEndVisitFlowEvent, emitEndVisitPhaseEvent } from "./monitoring";

/**
 * Timing for the end-visit flow: how long a provider spends on the interim screen, on the
 * end note screen, and on the whole trip from "End Visit" to "Sign Notes & End Visit".
 *
 * WHY captureEvent AND NOT SPANS. A ui.screen span is governed by tracesSampleRate, which is
 * 0.1 in production - nine of every ten encounter closes would vanish, and encounter closes are
 * low-volume and individually meaningful. The errors dataset is governed by neither
 * tracesSampleRate nor the replay rates, so every close the tab observes is recorded. This is
 * the same reasoning, and the same shape, as emitRefreshMeasurement for smart-summary refreshes.
 *
 * WHY MODULE SCOPE AND NOT REACT STATE. The flow starts in SCDHeader on the patient detail page
 * and ends in EndVisitStepper two screens later, across a react-router navigation that unmounts
 * the component that started it. Module state outlives that; component state does not.
 *
 * The start time is mirrored into sessionStorage so a mid-flow reload does not silently drop the
 * measurement. sessionStorage rather than localStorage deliberately: the flow belongs to this tab,
 * and a stale entry surviving a browser restart would report a bogus multi-day duration.
 */

export type EndVisitPhase = "interim_screen" | "end_note";

/**
 * How the provider arrived. Only "end_visit_click" flows have a real t0 on the patient detail
 * page; the others are deep links that skip it (Open Encounters, a resumed past visit, a
 * "Review Later" pickup). Reporting them together would make the flow duration meaningless, so
 * the entry point rides on every event and only end_visit_click flows emit a flow duration.
 */
export type EndVisitEntry = "end_visit_click" | "open_encounters" | "past_visit_resume" | "review_later" | "unknown";

export type EndVisitOutcome = "completed" | "abandoned" | "review_later";

const FLOW_STORAGE_KEY = "nl.endVisitFlow";

interface FlowState {
  startedAt: number;
  entry: EndVisitEntry;
  encounterRecordingId?: string;
  patientId?: string;
  // Wall-clock ms from t0 until the interim screen stopped showing its spinner. This is note
  // generation, not provider deliberation, and it is usually the largest part of the flow.
  generationMs?: number;
  // Wall-clock ms from t0 to pressing Accept. Recorded so a second trip through the interim
  // screen cannot report the same visit twice.
  acceptedMs?: number;
}

let flow: FlowState | null = null;

interface PhaseState {
  phase: EndVisitPhase;
  startedAt: number;
  encounterRecordingId?: string;
  patientId?: string;
  // Accumulated ms the tab spent hidden while this phase was open.
  hidden: HiddenTime;
  // When the screen stopped showing its spinner and had real content on it. Null while still
  // waiting. The interim screen opens on a spinner because note generation is still running, so
  // its wall-clock duration is waiting plus reviewing; this is what separates the two.
  readyAt: number | null;
}

let phaseState: PhaseState | null = null;

// Re-exported so the existing test seam keeps working: the clock now lives in dwellClock so one
// injection drives both timing services, but callers of this module should not have to know that.
export { __setClockForTests };

function readStoredFlow(): FlowState | null {
  return readStoredState<FlowState>(FLOW_STORAGE_KEY, (parsed) => typeof (parsed as FlowState | null)?.startedAt === "number");
}

function writeStoredFlow(state: FlowState | null): void {
  writeStoredState(FLOW_STORAGE_KEY, state);
}

function currentFlow(): FlowState | null {
  if (!flow) {
    flow = readStoredFlow();
  }
  return flow;
}

/**
 * t0 for measurement 3. Called from the "End Visit?" confirmation modal's OK handler, which is
 * the moment the provider commits: recording stops, upload starts, note generation begins.
 */
export function beginEndVisitFlow(ctx: { encounterRecordingId?: string; patientId?: string } = {}): void {
  flow = {
    startedAt: now(),
    entry: "end_visit_click",
    encounterRecordingId: ctx.encounterRecordingId,
    patientId: ctx.patientId,
  };
  writeStoredFlow(flow);
}

/**
 * Records a flow that began somewhere other than the End Visit button, so the entry point is
 * still reported even though there is no meaningful t0. Never overwrites a real end_visit_click
 * flow: a provider can reach the end note screen by Accept from a flow already in progress.
 */
export function noteEndVisitEntry(entry: EndVisitEntry, ctx: { encounterRecordingId?: string; patientId?: string } = {}): void {
  const existing = currentFlow();
  if (existing && existing.entry === "end_visit_click") return;
  flow = {
    startedAt: now(),
    entry,
    encounterRecordingId: ctx.encounterRecordingId,
    patientId: ctx.patientId,
  };
  writeStoredFlow(flow);
}

/**
 * Backfills identifiers onto a flow already in progress. The recording id does not exist yet when
 * beginEndVisitFlow runs - the upload that follows the confirmation click is what mints it - so
 * without this the flow event ships with no id and cannot be joined to its phase events. Only
 * fills blanks; never overwrites an id already recorded.
 */
export function attachEndVisitContext(ctx: { encounterRecordingId?: string; patientId?: string }): void {
  const state = currentFlow();
  if (!state) return;
  let changed = false;
  if (!state.encounterRecordingId && ctx.encounterRecordingId) {
    state.encounterRecordingId = ctx.encounterRecordingId;
    changed = true;
  }
  if (!state.patientId && ctx.patientId) {
    state.patientId = ctx.patientId;
    changed = true;
  }
  if (!changed) return;
  flow = state;
  writeStoredFlow(flow);
}

/**
 * t1: the interim screen has real content. Splits the flow into machine time (generation) and
 * provider time, which is the difference between a number about the pipeline and a number about
 * the user. First call wins - the loading gate can flap as react-query refetches.
 */
export function markEndVisitNoteReady(): void {
  // Stamp the open screen before the flow's first-call-wins early return. Belt and braces rather
  // than load-bearing: the only phase that reaches here unseeded is the first interim screen, and
  // at that point the flow has no generation reading either, so the return cannot fire. Moving
  // this below it changes nothing today - the ordering is here so that it stays harmless if the
  // seeding rule in enterEndVisitPhase is ever loosened.
  if (phaseState && phaseState.readyAt === null) {
    phaseState.readyAt = now();
  }

  const state = currentFlow();
  if (!state || state.generationMs !== undefined) return;
  state.generationMs = now() - state.startedAt;
  flow = state;
  writeStoredFlow(flow);
}

/**
 * Terminal for measurement 3. Emits only for flows that started at the End Visit button, since
 * a deep-linked flow has no t0 to measure from.
 */
export function endEndVisitFlow(outcome: EndVisitOutcome): void {
  // Close the open screen at the same instant the flow ends, before the flow state is cleared -
  // exitEndVisitPhase reads the flow's entry point.
  //
  // Without this the end note screen's clock ran until the URL changed, which happens when the
  // provider dismisses the success modal - not when they finish the note. Signing kicks off EMR
  // validation and then a success modal they may sit on printing a visit summary, all of it on
  // the same ?end_visit=2 URL, so the measurement absorbed the lot and the data point landed in
  // whichever minute they closed the modal.
  //
  // Known gap, not handled here: if signing fails EMR validation and the provider returns to
  // editing, the screen clock has already stopped and the flow is already cleared, so the extra
  // editing time goes unmeasured. That mirrors the existing choice to end the flow on the click
  // rather than on the API response, and unpicking it needs the validation-failure path to
  // reopen both.
  exitEndVisitPhase();

  const state = currentFlow();
  flow = null;
  writeStoredFlow(null);
  if (!state) return;
  if (state.entry !== "end_visit_click") return;

  emitEndVisitFlowEvent({
    outcome,
    entry: state.entry,
    durationMs: now() - state.startedAt,
    generationMs: state.generationMs,
    encounterRecordingId: state.encounterRecordingId,
    patientId: state.patientId,
  });
}

/**
 * Emits the End Visit click -> Accept measurement, which is the whole first half of the flow:
 * stopping the recording, uploading it, waiting for the note, and reviewing the structured data.
 *
 * Only for flows that began at the End Visit button, for the same reason endEndVisitFlow filters:
 * a deep-linked entry has no meaningful t0, so the duration would be measured from whenever the
 * hook happened to notice. Fires once - a second trip through the interim screen via the back
 * button re-Accepts, and reporting that again would count one visit twice.
 */
function reportAcceptReached(): void {
  const state = currentFlow();
  if (!state || state.entry !== "end_visit_click") return;
  if (state.acceptedMs !== undefined) return;

  state.acceptedMs = now() - state.startedAt;
  flow = state;
  writeStoredFlow(flow);

  emitEndVisitAcceptEvent({
    entry: state.entry,
    durationMs: state.acceptedMs,
    generationMs: state.generationMs,
    encounterRecordingId: state.encounterRecordingId,
    patientId: state.patientId,
  });
}

/**
 * Measurements 1 and 2. enterPhase on arrival, exitPhase on leaving; exitPhase is what emits, so
 * a phase left open by a hard tab close is lost. The pagehide flush in useEndVisitTiming covers
 * the common case of closing the tab on the end note screen.
 */
export function enterEndVisitPhase(phase: EndVisitPhase, ctx: { encounterRecordingId?: string; patientId?: string } = {}): void {
  if (phaseState?.phase === phase) return;

  // Interim screen -> end note is the Accept button, and nothing else reaches it: the only other
  // way to end_note is a deep link, which arrives with no interim phase open. Reported here
  // rather than composed later from generation + ready time, because adding two percentiles is
  // not arithmetic - the provider who waited longest for the note is rarely the one who reviewed
  // longest, so p75(a) + p75(b) is wrong by an amount nothing can recover.
  if (phaseState?.phase === "interim_screen" && phase === "end_note") {
    reportAcceptReached();
  }

  exitEndVisitPhase();
  phaseState = {
    phase,
    startedAt: now(),
    encounterRecordingId: ctx.encounterRecordingId,
    patientId: ctx.patientId,
    hidden: newHiddenTime(),
    // If the note has already generated, this screen has content the moment it opens, so it was
    // never waiting: the end note screen, and any return to the interim screen via the back
    // button. Seeding it here rather than waiting for markEndVisitNoteReady keeps the reading
    // deterministic - the stepper's content-ready effect and this hook's phase switch are
    // separate components' effects, with no guaranteed order between them.
    readyAt: currentFlow()?.generationMs === undefined ? null : now(),
  };
}

export function exitEndVisitPhase(): void {
  const state = phaseState;
  phaseState = null;
  if (!state) return;

  const at = now();
  const hiddenMs = hiddenMsThrough(state.hidden, at);
  const durationMs = at - state.startedAt;

  // A phase that opened and closed inside the same tick is a react remount or a redirect passing
  // through, not a provider looking at a screen. Reporting it would drag every percentile down.
  if (durationMs <= 0) return;

  emitEndVisitPhaseEvent({
    phase: state.phase,
    durationMs,
    // Time with real content on screen. Undefined means the screen was left while still
    // spinning - a provider who gave up on the wait - which is worth distinguishing from a
    // review that took no time at all, so it is omitted rather than sent as zero.
    readyMs: state.readyAt === null ? undefined : Math.max(0, at - state.readyAt),
    // Wall clock includes time the provider was in another tab or had the laptop shut. Both
    // numbers ship: duration_ms answers "how long was it open", active_ms answers "how long were
    // they actually looking at it", and on the end note screen those diverge a lot.
    activeMs: Math.max(0, durationMs - hiddenMs),
    entry: currentFlow()?.entry ?? "unknown",
    encounterRecordingId: state.encounterRecordingId,
    patientId: state.patientId,
  });
}

/** Called on visibilitychange so hidden time can be subtracted from the active figure. */
export function recordVisibilityChange(hidden: boolean): void {
  if (!phaseState) return;
  recordHidden(phaseState.hidden, hidden);
}

/** Test seam only. */
export function __resetEndVisitTimingForTests(): void {
  flow = null;
  phaseState = null;
  writeStoredFlow(null);
}
